import { type FC, useCallback } from 'react';

import { mapAirportName } from '@vakantiesnl/services/src/contentful/mapper';
import { convertMinsToHrsMins, getTimeFromDate } from '@vakantiesnl/services/src/util/dateUtils';
import { type Booking, type MicroCopy, type Search } from '@vakantiesnl/types/src';

import { useCustomStyles } from './Flight.styles';
import { getFlightTypeText } from './helpers';
import { Divider } from '../../atoms/Divider';
import { Icon } from '../../atoms/Icon';

export type FlightProps = {
	flight: Search.Flight | Booking.Flight | Booking.FlightSegment;
	isOutbound?: boolean;
	microCopies: MicroCopy;
	isTransfer?: boolean;
	onClickStopOverButton?: () => void;
	showLeadingAirPlaneIcons?: boolean;
};

export const Flight: FC<FlightProps> = (props) => {
	const {
		flight,
		isOutbound,
		isTransfer = false,
		microCopies,
		onClickStopOverButton,
		showLeadingAirPlaneIcons,
	} = props;
	const { classes, cx } = useCustomStyles();

	const hasStopOver = 'stopOver' in flight && flight.stopOver !== 0;

	const renderAirportText = useCallback(
		(airport: Booking.Airport) => (
			<div className={classes.airport}>
				<span>{mapAirportName(airport, microCopies)}</span> <span>{`(${airport.code})`}</span>
			</div>
		),
		[microCopies, classes.airport],
	);

	const renderArrivalTime = useCallback(
		() => <div className={classes.timestamp}>{getTimeFromDate(new Date(flight.arrivalDateTime))}</div>,
		[classes.timestamp, flight.arrivalDateTime],
	);

	return (
		<div className={classes.flight}>
			{showLeadingAirPlaneIcons && isOutbound && <Icon type="airplaneRight" />}
			{showLeadingAirPlaneIcons && !isOutbound && <Icon type="airplaneLeft" />}
			<div className={classes.flightDetails}>
				<div className={classes.flightRow}>
					<div className={classes.timeStampWithAirport}>
						<div className={classes.timestamp}>{getTimeFromDate(new Date(flight.departureDateTime))}</div>
						<div className={classes.isMobileOnly}>{renderAirportText(flight.departureAirport)}</div>
					</div>
					<Divider className={cx(classes.line, classes.isDesktopOnly)} noMargins />
					<>
						<div className={classes.duration}>
							{(!isTransfer || hasStopOver) && (
								<div className={classes.stopOver}>
									{!isTransfer && (
										<div
											className={cx(classes.flightType, {
												[classes.hasStopOver]: hasStopOver,
											})}
										>
											{getFlightTypeText(flight, microCopies)}
										</div>
									)}
									{hasStopOver && !!onClickStopOverButton && (
										<button
											type="button"
											onClick={onClickStopOverButton}
											className={classes.stopOverButton}
										>
											<Icon type="info" />
										</button>
									)}
								</div>
							)}
							<div className={classes.travelTime}>
								<span>
									{convertMinsToHrsMins(flight.duration)} {microCopies['travelTime']}
								</span>
								<div className={cx(classes.airline, classes.isMobileOnly)}>
									<Icon type="airline" />
									<span>
										{flight.airline && `${flight.airline.value}`}{' '}
										<span className={classes.airlineText}>{flight.displayFlightNumber}</span>
									</span>
								</div>
							</div>
						</div>
						<Divider className={cx(classes.line, classes.isDesktopOnly)} noMargins />
					</>
					<div className={classes.isDesktopOnly}>{renderArrivalTime()}</div>
				</div>
				<div className={cx(classes.flightRow, classes.flightNames)}>
					<div className={classes.isDesktopOnly}>{renderAirportText(flight.departureAirport)}</div>
					<div className={cx(classes.airline, classes.isDesktopOnly)}>
						<Icon type="airline" />
						<span>
							{flight.airline && `${flight.airline.value}`}{' '}
							<span className={classes.airlineText}>{flight.displayFlightNumber}</span>
						</span>
					</div>
					<div className={classes.timeStampWithAirport}>
						<div className={classes.isMobileOnly}>{renderArrivalTime()}</div>
						{renderAirportText(flight.arrivalAirport)}
					</div>
				</div>
			</div>
		</div>
	);
};
